'use client';

import { useEffect, useMemo } from 'react';
import type { MarketingOptInConfig } from '@impact/types';
import { trpc } from '@/lib/trpc';
import { useRegistrationStore } from '../../stores/registration-store';
import type { MarketingOptInsSource } from '../types';

/**
 * Resolves the opt-in checkbox config for the preferences step and seeds the
 * registration store with each key's defaultChecked. Values already present
 * in the store (user edits or checkoutState hydration) win over defaults.
 */
export function useMarketingOptIns(
  source: MarketingOptInsSource,
  eventId: string | null,
): { optIns: MarketingOptInConfig[]; isLoading: boolean } {
  const seedMarketingOptIns = useRegistrationStore((s) => s.seedMarketingOptIns);

  const campaignQuery = trpc.publicDonation.getActiveCampaign.useQuery(
    { eventId: eventId ?? undefined },
    { enabled: source.source === 'campaign' && Boolean(eventId) },
  );

  const optIns = useMemo<MarketingOptInConfig[]>(() => {
    if (source.source === 'static') return source.optIns;
    return campaignQuery.data?.marketingOptIns ?? [];
  }, [source, campaignQuery.data?.marketingOptIns]);

  useEffect(() => {
    if (optIns.length === 0) return;
    seedMarketingOptIns(
      Object.fromEntries(optIns.map((optIn) => [optIn.key, optIn.defaultChecked])),
    );
  }, [optIns, seedMarketingOptIns]);

  return {
    optIns,
    isLoading: source.source === 'campaign' && campaignQuery.isLoading,
  };
}
